import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { FC, memo } from 'react';
import { Button, Input, message, Space } from 'udesk-ui';
import { Editor, ItemTitle, ItemWrapper, Operator, QaRequirementsContent } from './components';
import TokenEstimate, { TokenEstimateRef } from './components/TokenEstimate';
import { estimateTokenCount, INPUT_TOKEN_OVERHEAD } from './tokenEstimate';
import VariableFieldSelect from 'src/components/common/VariableFieldSelect';
import { TemplatePropsValue as TypeSelectValue } from '../TypeSelect';
import { TypeEnum } from '../../const';
import Optimization from './components/Optimization';
import { FieldFoundResponse } from 'src/api/types';

interface PromptAreaProps {
    value?: string;
    onChange?: (v: string) => void;
    getParams?: () => { modelDesc: string; typeSelect: TypeSelectValue };
    setParams?: (d: any) => void;
    formatOutput?: string;
}

let tempValue = '';

// Token 预估的防抖时长，避免大内容时阻塞输入
const TOKEN_ESTIMATE_DEBOUNCE = 300;

const PromptArea: FC<PromptAreaProps> = (props) => {
    const { value, onChange, getParams, setParams, formatOutput } = props;

    const editorRef = React.useRef<HTMLDivElement>(null);
    const tokenEstimateRef = React.useRef<TokenEstimateRef>(null);
    const tokenEstimateTimerRef = React.useRef<number>();

    const renderTokenEstimate = () => {
        // 直接读取编辑器 DOM 文本，避免重复解析 HTML
        const promptText = editorRef.current?.textContent || '';
        // 输入侧预估 = 提示语模板 + 输出规范 + 固定系统开销
        // 不含运行时注入的变量真实数据（如整通对话原文）
        const total =
            estimateTokenCount(promptText) +
            estimateTokenCount(formatOutput || '') +
            INPUT_TOKEN_OVERHEAD;
        // 预估输入约 x Token（按项目规则暂无对应翻译，中文硬编码）
        // no-locale-translate
        tokenEstimateRef.current?.update(`预估输入约 ${total.toLocaleString()} Token`);
    };

    const scheduleTokenEstimate = () => {
        if (tokenEstimateTimerRef.current) {
            clearTimeout(tokenEstimateTimerRef.current);
        }
        tokenEstimateTimerRef.current = window.setTimeout(
            renderTokenEstimate,
            TOKEN_ESTIMATE_DEBOUNCE
        );
    };

    // 外部内容变化（初始化回填、生成提示语等）后重新预估
    React.useEffect(() => {
        scheduleTokenEstimate();
        return () => {
            if (tokenEstimateTimerRef.current) {
                clearTimeout(tokenEstimateTimerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, formatOutput]);

    const onGenPrompt = () => {
        const { modelDesc, typeSelect } = getParams?.() || {};
        const { type, divideRuleList } = typeSelect || {};

        if (
            !modelDesc ||
            (type === TypeEnum.GradedEvaluation &&
                !divideRuleList?.every((e) => e.divide && e.divideDesc))
        ) {
            message.warning(
                /* 请完善配置 */ UdeskLocales['current'].pages.semanticIntelligence.intelligentModel
                    .components.promptArea.index.pleaseImproveTheConfiguration
            );
            return;
        }

        if (type === TypeEnum.GradedEvaluation) {
            onChange?.(genGradedEvaluationPrompt(modelDesc, divideRuleList));
        } else {
            onChange?.(genConformPrompt(modelDesc));
        }
    };

    const genConformPrompt = (desc: string) => {
        const isZh = Udesk.data.init.userLanguage === 'zh';

        if (isZh) {
            // no-locale-translate
            return `
                以上内容中 <span id="prompt-des" style="color: blue">${desc}</span>
                <br />
                <br />
            `;
        } else {
            return `
                In the above <span id="prompt-des" style="color: blue">${desc}</span>
                <br />
                <br />
            `;
        }
    };

    const genGradedEvaluationPrompt = (
        desc: string,
        divideRuleList?: TypeSelectValue['divideRuleList']
    ) => {
        const evaluatesLen = divideRuleList?.length;

        const isZh = Udesk.data.init.userLanguage === 'zh';

        if (isZh) {
            // no-locale-translate
            return `
                以上内容中 <span id="prompt-des" style="color: blue">${desc}</span>
                分为${evaluatesLen || 0}档评级
                <br />
                ${divideRuleList?.reduce(
                    (pre, cur, idx) =>
                        `${pre}
                        <span style="color: blue">
                            (${idx + 1})
                            "<span class="prompt-level">${
                                cur.divide || ''
                            }</span>"：<span class="prompt-eva">${cur.divideDesc || ''}</span>
                        </span>
                        <br />`,
                    ''
                )} 
                <br />
                请根据对话内容，从中选定一个评级（<span style="font-weight: 500">${divideRuleList
                    ?.map((d) => `<span>${d.divide || ''}</span>`)
                    .join('/')}</span>），并给出证明。
            `;
        } else {
            return `
                In the above <span id="prompt-des" style="color: blue">${desc}</span>
                It is rated in ${evaluatesLen || 0}
                <br />
                ${divideRuleList?.reduce(
                    (pre, cur, idx) =>
                        `${pre}
                        <span style="color: blue">
                            (${idx + 1})
                            "<span class="prompt-level">${
                                cur.divide || ''
                            }</span>": <span class="prompt-eva">${cur.divideDesc || ''}</span>
                        </span>
                        <br />`,
                    ''
                )} 
                <br />
                Select a rating based on the conversation (<span style="font-weight: 500">${divideRuleList
                    ?.map((d) => `<span>${d.divide || ''}</span>`)
                    .join('/')}</span>), and prove it.
            `;
        }
    };

    const onApply = (p?: string) => {
        onChange?.(p || '');
    };

    const onInput = (e) => {
        const modelDesc = e.target.querySelector('#prompt-des')?.innerText;
        setParams?.({ modelDesc });

        const levelSpans = Array.from(e.target.querySelectorAll('.prompt-level')) as any[];
        const evaSpans = Array.from(e.target.querySelectorAll('.prompt-eva')) as any[];
        if (levelSpans?.length && evaSpans?.length) {
            const divideRuleList = levelSpans.map((l, idx) => {
                return {
                    divide: l?.innerText,
                    divideDesc: evaSpans[idx]?.innerText,
                };
            });
            setParams?.({ typeSelect: { divideRuleList } });
        }

        const str = e.target.innerHTML;

        onChange?.(str.length && str !== '<br>' ? str : undefined);
        tempValue = str;

        scheduleTokenEstimate();
    };

    const onChooseField = (
        inspectDataSource?: number,
        type?: number,
        field?: FieldFoundResponse
    ) => {
        // 对于语言字段23和客户信息字段16，传参有变化
        const passType = type === 16 ? 5 : type === 23 ? 4 : type;

        const fieldPlaceholder = `<span contenteditable="false" style="color: #1b6dff; user-select: none;">[<span style="display: none">${inspectDataSource}:${passType}:</span>${field?.label}]</span>`;

        if (editorRef.current) {
            const selection = window.getSelection();

            // 锚点：选区的起始点
            let node = selection?.anchorNode;
            while (node && node !== editorRef.current) {
                node = node?.parentNode;
            }

            if (node === editorRef.current) {
                // 选中了编辑器
                if (selection) {
                    const range = selection.getRangeAt(0);
                    range.deleteContents();
                    const spanNode = document.createElement('span');
                    spanNode.innerHTML = fieldPlaceholder ? `&nbsp;${fieldPlaceholder}&nbsp;` : '';
                    range.insertNode(spanNode);
                    range.setStartAfter(spanNode);
                    range.setEndAfter(spanNode);
                    selection.removeAllRanges();
                    selection.addRange(range);
                    editorRef.current?.focus();
                    onInput({ target: editorRef.current });
                }
            } else {
                // 未选中编辑器
                onChange?.(
                    (editorRef.current.innerHTML || '') +
                        `<span>&nbsp;${fieldPlaceholder}&nbsp;</span>`
                );
            }
        }
    };

    return (
        <>
            <Operator>
                <Space>
                    <Button type="primary" onClick={onGenPrompt}>
                        {/* 生成提示语 */}
                        {
                            UdeskLocales['current'].pages.semanticIntelligence.intelligentModel
                                .components.promptArea.index.generatePromptLanguage
                        }
                    </Button>

                    <Optimization onApply={onApply} getParams={getParams} />
                </Space>
            </Operator>

            <ItemWrapper>
                <ItemTitle>
                    <span>
                        {/* 质检要求 */}
                        {
                            UdeskLocales['current'].pages.semanticIntelligence.intelligentModel
                                .components.promptArea.index.qualityInspectionRequirements
                        }
                    </span>

                    <TokenEstimate ref={tokenEstimateRef} />
                </ItemTitle>

                <QaRequirementsContent>
                    <Editor
                        ref={editorRef}
                        contentEditable
                        onInput={onInput}
                        dangerouslySetInnerHTML={{ __html: value }}
                        key={Math.random().toString()} // 在相同内容时强制刷新
                    ></Editor>

                    <VariableFieldSelect
                        onChooseField={onChooseField}
                        typeList={[1, 2, 3, 6, 16, 23]}
                    />
                </QaRequirementsContent>
            </ItemWrapper>

            {formatOutput ? (
                <ItemWrapper>
                    <ItemTitle>
                        {/* 输出规范 */}
                        {
                            UdeskLocales['current'].pages.semanticIntelligence.intelligentModel
                                .components.promptArea.index.outputSpecification
                        }
                    </ItemTitle>

                    <Input.TextArea
                        style={{ resize: 'none', backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
                        autoSize
                        readOnly
                        value={formatOutput}
                    />
                </ItemWrapper>
            ) : null}
        </>
    );
};

export default memo(PromptArea, (prev, next) => {
    // 防止闪烁
    return tempValue === next.value;
});
